// ENG-2277 (HTML Forms): what a surface (Repair, Order, Exchange, Loss & Damage, Loaner, Patient or Appointment
// Forms) needs to offer its digital Forms: is the picked row digital, are its merge tags
// ready, and open it. Mounting this hook is what loads the merge-tag data (the patient, the signature
// images), so a surface mounts it only once a digital row is picked: in SurfaceDigitalFormButton, or
// in a button of its own.
import { useCallback, useRef } from "react";

import { isDigitalForm } from "components/form-templates-field/digital-forms.utils";
import type { MergeTagExtraData } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import useMergeTags from "components/word-processor-field/merge-tags/use-merge-tags.hook";
import type { Appointment } from "services/types/appointments.type";
import type { FormTemplate } from "services/types/form-templates.type";

import type { CustomPrintFormMetadata } from "./use-custom-print-form-preview.hook";
import { useOpenDigitalForm } from "./use-open-digital-form.hook";

type UseSurfaceDigitalFormParams = {
  /** HTML_FORMS_FEATURE: off, the surface behaves exactly as before. */
  enabled: boolean;
  /** The surface (a FormTemplateType), the same value passed to FormTemplatesField. */
  placement: number;
  patientId?: number;
  /** The row picked in the surface's FormTemplatesField. */
  selected?: FormTemplate | null;
  /** The surface's own merge-tag values (S3), e.g. { request }, { lossDamage }, { order }. */
  additionalMergeTagsValues?: MergeTagExtraData;
  /** Appointment.Provider.* tags resolve to this appointment, not the next one. For Appointment Forms. */
  boundAppointment?: Appointment;
  metadata?: Omit<
    CustomPrintFormMetadata,
    "additionalMergeTagsValues" | "formSettings"
  >;
};

export const useSurfaceDigitalForm = ({
  enabled,
  placement,
  patientId,
  selected,
  additionalMergeTagsValues,
  boundAppointment,
  metadata
}: UseSurfaceDigitalFormParams) => {
  const isDigitalSelected = enabled && isDigitalForm(selected);
  // A stable ref, so the merge tags' data source survives re-renders of the surface.
  const fieldIdRef = useRef<string | undefined>(undefined);
  const { isReady, getMergeTagValue } = useMergeTags({
    fieldIdRef,
    allowMergeTags: isDigitalSelected,
    allowInsertMergeTagsValues: isDigitalSelected,
    allowHtml: true,
    patientId,
    additionalMergeTagsValues,
    boundAppointment
  });
  const { openDigitalForm, isOpening } = useOpenDigitalForm();

  const open = useCallback(
    (onUnavailable?: () => void) => {
      if (!isDigitalSelected || !selected || !patientId)
        return Promise.resolve(false);
      return openDigitalForm({
        option: selected,
        placement,
        patientId,
        getMergeTagValue,
        additionalMergeTagsValues,
        metadata,
        onUnavailable
      });
    },
    [
      additionalMergeTagsValues,
      getMergeTagValue,
      isDigitalSelected,
      metadata,
      openDigitalForm,
      patientId,
      placement,
      selected
    ]
  );

  return {
    /** Show "Edit Form & Print" in place of the surface's Anvil buttons. */
    isDigitalSelected,
    /** Merge-tag data for the patient is loaded; gate the button on it. */
    isReady: isDigitalSelected && isReady,
    isOpening,
    open
  };
};
